import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import type { AppDispatch } from "@/app/store";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { pickStepUpState } from "@/shared/utils/stepUp";
import { colocaToast } from "@/shared/utils/toast";

import { authService } from "../services/authService";
import { updateStepUpState } from "../store/authSlice";
import { thunkDoLogout } from "../store/authThunks";

/** Long enough to be read on the login screen the person lands on. */
const SESSIONS_REVOKED_TOAST_MS = 12_000;

/**
 * The PIN's tenth wrong attempt (401 `SECURITY_PIN_RESET_REQUIRED`, announced by `HttpApiService` as
 * `auth:step-up-reset-required`) also closes every session of the user server-side: whoever was guessing has
 * the session, not the password. Left alone, every later request would fail one by one and the screen would
 * fall apart. So the session is checked once: if `/auth/me` still answers, only the step-up state is
 * refreshed (the challenge already shows the way to a new PIN); if it answers 401, the person is told why and
 * signed out to the login, where a new PIN is one sign-in away.
 */
export function useStepUpLockoutWatcher(): void {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslations("global");

  useEffect(() => {
    let checking = false;

    const handler = async () => {
      // A burst of refused requests is one fact: check the session once.
      if (checking) return;
      checking = true;
      try {
        const me = await authService.getMe();
        dispatch(updateStepUpState(pickStepUpState(me)));
      } catch (error: unknown) {
        // Only a refused session means the sessions were closed; a network failure is not a reason to sign out.
        if (!HttpApiService.isUnauthorizedError(error)) return;
        colocaToast.error(t("step_up_sessions_revoked"), { duration: SESSIONS_REVOKED_TOAST_MS });
        // Typed as `void`, but it is the async thunk: wait for it so the login screen never bounces back.
        await (dispatch(thunkDoLogout()) as unknown as Promise<void>);
        navigate("/auth/login", { replace: true });
      } finally {
        checking = false;
      }
    };

    const listener = () => void handler();
    window.addEventListener("auth:step-up-reset-required", listener);
    return () => window.removeEventListener("auth:step-up-reset-required", listener);
  }, [dispatch, navigate, t]);
}
